<template>
  <div class="news-container">
    <v-card class="news-card" elevation="0">
      <div class="card-header-modern">
        <div class="header-content">
          <div class="header-left">
            <v-icon class="header-icon" color="#1e3a8a">mdi-bell-ring</v-icon>
            <div class="header-text">
              <h3 class="card-title-modern">공지사항</h3>
              <p class="card-subtitle">중요한 알림과 업데이트</p>
            </div>
          </div>
          <router-link to="/pgm/notice" class="view-all-link">
            전체보기
            <v-icon size="16">mdi-chevron-right</v-icon>
          </router-link>
        </div>
      </div>
      
      <div class="news-content">
        <!-- 로딩 -->
        <div v-if="loading" class="loading-state">
          <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
        </div>
        
        <!-- 공지사항 목록 -->
        <div 
          v-else-if="newsList.length > 0"
          v-for="(news, index) in newsList" 
          :key="index"
          class="news-item"
          :class="{ 'important': news.nc_category === 'URGENT' }"
          @click="goToDetail(news.nc_idx)"
        >
          <div class="news-icon-wrapper" :class="getTypeClass(news.nc_category)">
            <v-icon :color="getIconColor(news.nc_category)" size="20">
              {{ getIcon(news.nc_category) }}
            </v-icon>
          </div>
          <div class="news-details">
            <div class="news-header">
              <span class="news-title">{{ news.nc_title }}</span>
              <v-chip 
                v-if="news.nc_category === 'URGENT'" 
                size="x-small" 
                color="error" 
                variant="flat"
                class="important-badge"
              >
                긴급
              </v-chip>
              <v-chip 
                v-else-if="isNew(news.nc_wdate)" 
                size="x-small" 
                color="primary" 
                variant="flat"
                class="important-badge"
              >
                NEW
              </v-chip>
            </div>
            <p class="news-description">{{ news.nc_type === 'ADMIN' ? '관리자' : news.nc_sisul_name || '-' }}</p>
            <span class="news-date">{{ formatDate(news.nc_wdate) }}</span>
          </div>
        </div>
        
        <div v-else class="empty-state">
          <v-icon size="48" color="grey-lighten-2">mdi-bulletin-board</v-icon>
          <p class="empty-text">현재 공지사항이 없습니다</p>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: string;
  nc_category: string;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_wdate: string;
}

const router = useRouter();
const loading = ref(false);
const newsList = ref<Notice[]>([]);

// 공지사항 로드
const loadNotices = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/notice/list', {
      params: { page: 1, limit: 5 },
      withCredentials: true
    });
    
    if (response.data.success) {
      newsList.value = response.data.notices;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

// 상세 페이지로 이동
const goToDetail = (ncIdx: number) => {
  router.push(`/pgm/notice/${ncIdx}`);
};

// 유틸리티 함수들
const formatDate = (date: string) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const isNew = (date: string) => {
  if (!date) return false;
  const d = new Date(date);
  const now = new Date();
  return (now.getTime() - d.getTime()) < 3 * 24 * 60 * 60 * 1000;
};

const getTypeClass = (category: string) => {
  switch (category) {
    case 'NOTICE': return 'info';
    case 'UPDATE': return 'update';
    case 'EVENT': return 'success';
    case 'URGENT': return 'warning';
    default: return 'info';
  }
};

const getIcon = (category: string): string => {
  const icons: Record<string, string> = {
    NOTICE: 'mdi-information',
    UPDATE: 'mdi-update',
    EVENT: 'mdi-party-popper',
    URGENT: 'mdi-alert'
  };
  return icons[category] || 'mdi-information';
};

const getIconColor = (category: string): string => {
  const colors: Record<string, string> = {
    NOTICE: '#1e3a8a',
    UPDATE: '#0097a7',
    EVENT: '#388e3c',
    URGENT: '#d32f2f'
  };
  return colors[category] || '#1e3a8a';
};

onMounted(() => {
  loadNotices();
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/newsList.scss';

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.view-all-link {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #1e3a8a;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.news-item {
  cursor: pointer;
  
  &:hover {
    background: #f5f5f5;
  }
}
</style>
